import { html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { api, send, type Schemas } from '../lib/api';
import { icon } from '../lib/icons';
import { LightElement } from '../lib/light-element';
import { appUrl } from '../lib/page';
import { filterHref } from '../lib/photo-filter';

export function isApplePlatform(platform: string): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(platform);
}

export function isSearchShortcut(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): boolean {
  return e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
}

export interface SearchEventDetail {
  query: string;
}

type PhotoResults = Schemas['PhotoSearchResults'];
type RecipeResults = Schemas['RecipeSearchResults'];
type PeopleResults = Schemas['PeopleSearchResults'];

/**
 * Global search field in the Overview header (spec §4). Ctrl+K / ⌘K focuses it from anywhere.
 * Submitting dispatches a bubbling `hl-search` event and asks each portal that provides search for its top matches:
 * today Photos (a row of thumbnails and a link to all matches). A portal the user can't use simply contributes nothing.
 */
@customElement('hl-global-search')
export class HlGlobalSearch extends LightElement {
  @property()
  placeholder = 'Search photos, recipes, people, devices…';

  /** Overridable for tests; defaults to the browser's platform string. */
  @property({ attribute: false })
  platform = navigator.platform;

  @query('input')
  private input!: HTMLInputElement;

  @state() private searched = '';
  @state() private photos: PhotoResults | null = null;
  @state() private recipes: RecipeResults | null = null;
  @state() private people: PeopleResults | null = null;
  @state() private busy = false;
  private request = 0;

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (isSearchShortcut(e)) {
      e.preventDefault();
      this.input.focus();
      this.input.select();
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keydown', this.onKeyDown);
  }

  override disconnectedCallback(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    super.disconnectedCallback();
  }

  private onSubmit(e: SubmitEvent) {
    e.preventDefault();
    const query = this.input.value.trim();
    if (query) {
      this.dispatchEvent(new CustomEvent<SearchEventDetail>('hl-search', { detail: { query }, bubbles: true, composed: true }));
      void this.searchPortals(query);
    }
  }

  private async searchPortals(query: string) {
    const request = ++this.request;
    this.searched = query;
    this.busy = true;
    // Each portal answers on its own; one the user can't use (or that fails) simply contributes nothing.
    const [photos, recipes, people] = await Promise.all([
      send(() => api().GET('/api/photos/search', { params: { query: { q: query, limit: 6 } } })).then((r) => (r.ok ? r.data : null), () => null),
      send(() => api().GET('/api/recipes/search', { params: { query: { q: query, limit: 4 } } })).then((r) => (r.ok ? r.data : null), () => null),
      send(() => api().GET('/api/people/search', { params: { query: { q: query, limit: 4 } } })).then((r) => (r.ok ? r.data : null), () => null),
    ]);
    if (request !== this.request) return;
    this.photos = photos;
    this.recipes = recipes;
    this.people = people;
    this.busy = false;
  }

  private closeResults() {
    this.request++;
    this.searched = '';
    this.photos = null;
    this.recipes = null;
    this.people = null;
    this.busy = false;
  }

  private renderResults() {
    if (!this.searched) return nothing;
    const { photos, recipes, people } = this;
    const hasPhotos = !!photos && photos.total > 0;
    const hasRecipes = !!recipes && recipes.total > 0;
    const hasPeople = !!people && people.total > 0;
    return html`
      <div class="hl-search-results" role="region" aria-label="Search results" aria-busy=${this.busy} data-testid="global-search-results">
        ${this.busy
          ? html`<p class="hl-search-note">Searching…</p>`
          : html`
            ${hasPhotos
              ? html`<h2 class="hl-search-group">Photos</h2>
                <div class="hl-search-photos">
                  ${photos.items.map((p) => html`<a href=${appUrl(`photos?item=${p.id}`)} title=${p.name} data-testid="global-search-photo">
                    <img src=${appUrl(`api/photos/${p.id}/thumb`)} alt=${p.name} loading="lazy" /></a>`)}
                </div>
                <a class="hl-search-all" href=${filterHref({ q: this.searched })} data-testid="global-search-photos-all">See all ${photos.total} in Photos</a>`
              : nothing}
            ${hasRecipes
              ? html`<h2 class="hl-search-group" data-testid="global-search-recipes-title">Recipes</h2>
                <ul class="hl-search-recipes">
                  ${recipes.items.map((r) => html`<li><a href=${appUrl(`recipes/${r.id}`)} data-testid="global-search-recipe">
                    <span class="hl-search-recipe-photo">${r.photoKey
                      ? html`<img src=${appUrl(`api/recipes/${r.id}/photo?size=card&v=${r.photoKey}`)} alt="" loading="lazy" />`
                      : icon('recipes', 18)}</span>
                    <span class="hl-search-recipe-title">${r.title}</span></a></li>`)}
                </ul>
                <a class="hl-search-all" href=${appUrl(`recipes?q=${encodeURIComponent(this.searched)}`)} data-testid="global-search-recipes-all">See all ${recipes.total} in Recipes</a>`
              : nothing}
            ${hasPeople
              ? html`<h2 class="hl-search-group" data-testid="global-search-people-title">People</h2>
                <ul class="hl-search-recipes">
                  ${people.items.map((p) => html`<li><a href=${appUrl(`people/${p.id}`)} data-testid="global-search-person">
                    <span class="hl-search-recipe-photo">${p.photoKey
                      ? html`<img src=${appUrl(`api/people/${p.id}/photo?size=avatar&v=${p.photoKey}`)} alt="" loading="lazy" />`
                      : icon('people', 18)}</span>
                    <span class="hl-search-recipe-title">${p.displayName}</span></a></li>`)}
                </ul>
                <a class="hl-search-all" href=${appUrl(`people?q=${encodeURIComponent(this.searched)}`)} data-testid="global-search-people-all">See all ${people.total} in People</a>`
              : nothing}
            ${!hasPhotos && !hasRecipes && !hasPeople ? html`<p class="hl-search-note" data-testid="global-search-none">Nothing found for “${this.searched}”.</p>` : nothing}`}
      </div>`;
  }

  private onInputKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.closeResults();
      this.input.blur();
    }
  }

  override render() {
    const hint = isApplePlatform(this.platform) ? '⌘K' : 'Ctrl K';
    return html`
      <form class="hl-search" role="search" @submit=${this.onSubmit}>
        ${icon('search', 17)}
        <input type="search" aria-label="Search" placeholder=${this.placeholder} data-testid="global-search"
          @keydown=${this.onInputKeyDown} />
        <kbd class="hl-kbd" aria-hidden="true">${hint}</kbd>
      </form>
      ${this.renderResults()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hl-global-search': HlGlobalSearch;
  }
  interface HTMLElementEventMap {
    'hl-search': CustomEvent<SearchEventDetail>;
  }
}
